import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="pb-8 border-b border-white/[0.06] last:border-0">
      <h2 className="text-xl font-semibold tracking-[-0.02em] mb-4 text-white">{title}</h2>
      <div className="text-white/50 text-base leading-[1.8]">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <PublicPageLayout>
      <SEO 
        title="Terms of Service | Sorvaine"
        description="Review Sorvaine's terms of service for using our proposal intelligence platform and related services."
        canonical="https://sorvaine.com/terms"
      />

      <section className="py-20 md:py-28 px-6">
        <motion.div 
          className="max-w-[800px] mx-auto"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-white/50 font-medium">Legal</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.04em] leading-tight mb-4">Terms of Service</h1>
          <p className="text-white/40 text-sm mb-16">Last updated: March 12, 2026</p>

          <div className="space-y-12">
            <LegalSection title="1. Acceptance of Terms">
              <p>By accessing or using the proposal intelligence platform ("Service") operated by Sorvaine - FZCO, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.</p>
            </LegalSection>

            <LegalSection title="2. Description of Service">
              <p>Sorvaine - FZCO provides a proposal intelligence platform that helps organizations create, manage, and optimize their proposal and bid responses. The Service includes content management, AI-powered assistance, analytics, and collaboration features.</p>
            </LegalSection>

            <LegalSection title="3. User Accounts">
              <p className="mb-4">To use the Service, you must:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Create an account with accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly notify us of any unauthorized use</li>
                <li>Be at least 18 years old and have the authority to bind your organisation to these Terms</li>
              </ul>
            </LegalSection>

            <LegalSection title="4. Acceptable Use">
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights of others</li>
                <li>Upload malicious code or attempt to compromise security</li>
                <li>Use the Service for any fraudulent or deceptive purposes</li>
                <li>Resell or redistribute the Service without authorization</li>
                <li>Interfere with other users' access to the Service</li>
              </ul>
            </LegalSection>

            <LegalSection title="5. Intellectual Property">
              <p>The Service and its original content, features, and functionality are owned by Sorvaine - FZCO and are protected by international copyright, trademark, and other intellectual property laws. Your content remains your property, but you grant us a license to use it to provide the Service. This license does not permit Sorvaine - FZCO to use your content to train AI models or share your content with third parties outside of what is necessary to deliver the Service. All AI-generated content produced by the Service using your data is owned by you. Sorvaine - FZCO makes no claim of ownership over outputs generated on your behalf.</p>
            </LegalSection>

            <LegalSection title="6. Payment Terms">
              <p>The following payment terms apply to all paid subscriptions. Paid subscriptions are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law. We reserve the right to change pricing with 30 days' notice. Sorvaine - FZCO's total liability to you shall not exceed the fees paid by you in the 12 months preceding the claim.</p>
            </LegalSection>

            <LegalSection title="7. Termination">
              <p>We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties. Upon termination, your right to use the Service will cease immediately.</p>
            </LegalSection>

            <LegalSection title="8. Disclaimer of Warranties">
              <p>The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free.</p>
            </LegalSection>

            <LegalSection title="9. Limitation of Liability">
              <p>To the maximum extent permitted by law, Sorvaine - FZCO shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service. In the event of a data breach affecting your personal data, we will notify you within 72 hours of becoming aware of the breach.</p>
            </LegalSection>

            <LegalSection title="10. Changes to Terms">
              <p>We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page. Your continued use of the Service after changes constitutes acceptance.</p>
            </LegalSection>

            <LegalSection title="11. Governing Law">
              <p>These Terms shall be governed by and construed in accordance with the laws of the Emirate of Dubai and the applicable federal laws of the United Arab Emirates. Sorvaine - FZCO is a company registered in the IFZA free zone, Dubai, UAE. Any disputes shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.</p>
            </LegalSection>

            <LegalSection title="12. Contact Us">
              <p>
                If you have any questions about these Terms, please contact us at{" "}
                <a href="mailto:hello@sorvaine.com" className="text-primary hover:underline">hello@sorvaine.com</a>.
              </p>
            </LegalSection>
          </div>
        </motion.div>
      </section>
    </PublicPageLayout>
  );
}
